import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle2, ShieldCheck, Compass, MessagesSquare } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { OperatingSystemBanner } from '../components/brand/OperatingSystemBanner';
import { ServiceOperatorStrip } from '../components/brand/ServiceOperatorStrip';
import { Seo } from '../components/seo/Seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '../components/ui/breadcrumb';
import { SERVICES } from '../lib/serviceData';
import { BRAND } from '../lib/siteConfig';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../lib/seoSchemas';

const STEPS = [
  { icon: Compass, n: '1', title: 'Intake on one stack', desc: 'Hospital case manager, agency intake coordinator, or family kicks off the episode. FHIR + faxed referrals are AI-triaged into a clean care plan.' },
  { icon: MessagesSquare, n: '2', title: 'Match & schedule', desc: 'CaraLoom\u2019s AI surfaces the right vetted clinician for the clinical need, schedule, and location. Open shifts auto-broadcast to qualified staff.' },
  { icon: ShieldCheck, n: '3', title: 'Care, billing & family in sync', desc: 'AI-assisted visit notes, real-time family updates, automatic 837P/835 + statements \u2014 every step coordinated on one HIPAA-ready operating system.' },
];

export default function ServiceLandingPage() {
  const { slug } = useParams();
  const svc = SERVICES[slug];
  if (!svc) return <Navigate to="/services" replace />;

  const path = `/services/${svc.slug}`;
  const Icon = svc.icon;
  const schema = [
    serviceSchema({ name: svc.name, description: svc.metaDescription, slug: svc.slug }),
    faqSchema(svc.faqs),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Care services', path: '/services' },
      { name: svc.name, path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="service-landing-page">
      <Seo
        title={svc.metaTitle}
        description={svc.metaDescription}
        path={path}
        image={svc.heroImage}
        keywords={svc.keywords}
        schema={schema}
        noindex
      />
      <PublicNavbar />
      <OperatingSystemBanner />

      {/* Breadcrumb */}
      <div className="cn-container pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/services">Care services</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{svc.name}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero */}
      <section className="cn-container pt-8 pb-14">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="cn-eyebrow mb-3 flex items-center gap-2">
              <Icon className="w-4 h-4 text-cn-accent" /> {svc.eyebrow}
            </div>
            <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-5">{svc.h1}</h1>
            <p className="text-lg text-cn-text-muted leading-relaxed max-w-xl">{svc.intro[0]}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/auth" className="cn-btn cn-btn-primary" data-testid="service-get-started">
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="cn-trust-badge"><ShieldCheck className="w-3.5 h-3.5" /> Background-checked</span>
              <span className="cn-trust-badge"><CheckCircle2 className="w-3.5 h-3.5" /> Verified & insured</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden cn-card aspect-[4/3]">
              <img src={svc.heroImage} alt={svc.heroAlt} className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="cn-container pb-12">
        <div className="max-w-3xl space-y-5">
          {svc.intro.slice(1).map((p, i) => (
            <p key={i} className="text-base sm:text-lg text-cn-text-muted leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      {/* Operator strip — dual-funnel to agencies/hospitals & clinicians */}
      <ServiceOperatorStrip serviceName={svc.name} />

      {/* Included + Who for */}
      <section className="cn-container pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="cn-card p-7 rounded-3xl">
            <h2 className="cn-display text-2xl text-cn-text mb-5">{svc.included.title}</h2>
            <ul className="space-y-3">
              {svc.included.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-cn-text">
                  <Check className="w-5 h-5 text-cn-primary mt-0.5 flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="cn-card p-7 rounded-3xl bg-cn-soft/40">
            <h2 className="cn-display text-2xl text-cn-text mb-5">{svc.whoFor.title}</h2>
            <ul className="space-y-3">
              {svc.whoFor.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-cn-text">
                  <CheckCircle2 className="w-5 h-5 text-cn-accent mt-0.5 flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Why CaraLoom */}
      <section className="bg-cn-primary text-white py-16">
        <div className="cn-container">
          <h2 className="cn-display text-3xl text-white mb-10">Why families choose {BRAND}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {svc.benefits.map((b, i) => (
              <div key={i} className="bg-white/10 rounded-2xl p-6 border border-white/15">
                <h3 className="font-semibold text-lg mb-2">{b.title}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="cn-container py-16">
        <h2 className="cn-display text-3xl text-cn-text mb-10">How {BRAND} works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="cn-card p-7 rounded-3xl">
              <div className="w-11 h-11 rounded-full bg-cn-soft flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-cn-primary" />
              </div>
              <div className="cn-eyebrow mb-2">Step {s.n}</div>
              <h3 className="font-semibold text-lg text-cn-text mb-2">{s.title}</h3>
              <p className="text-sm text-cn-text-muted leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Nationwide availability */}
      <section className="cn-container pb-16">
        <div className="cn-card p-7 rounded-3xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="cn-display text-2xl text-cn-text mb-2">Available nationwide</h2>
            <p className="text-cn-text-muted max-w-xl">CaraLoom is the operating system underneath hundreds of post-acute providers across the United States delivering {svc.name.toLowerCase()} — matched to clinical need, schedule, and geography.</p>
          </div>
          <Link to="/pricing" className="cn-btn cn-btn-ghost flex-shrink-0" data-testid="service-find-clinician">
            See operator pricing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="cn-container pb-16">
        <h2 className="cn-display text-3xl text-cn-text mb-8">Frequently asked questions</h2>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {svc.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`service-faq-${i}`}>
                <AccordionTrigger className="text-left text-cn-text font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-cn-text-muted leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related services */}
      <section className="cn-container pb-16">
        <h2 className="cn-display text-2xl text-cn-text mb-6">Related services</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {svc.related.map((rs) => {
            const r = SERVICES[rs];
            if (!r) return null;
            const RIcon = r.icon;
            return (
              <Link key={rs} to={`/services/${rs}`} className="cn-card cn-card-hover p-6 rounded-3xl block" data-testid={`related-service-${rs}`}>
                <div className="w-10 h-10 rounded-xl bg-cn-soft flex items-center justify-center mb-3">
                  <RIcon className="w-5 h-5 text-cn-primary" />
                </div>
                <h3 className="font-semibold text-cn-text mb-1">{r.name}</h3>
                <span className="text-sm text-cn-accent font-medium inline-flex items-center gap-1">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="cn-container pb-20">
        <div className="rounded-3xl bg-cn-primary text-white p-10 md:p-14 text-center">
          <h2 className="cn-display text-3xl md:text-4xl text-white mb-3">Run {svc.name.toLowerCase()} on the post-acute OS</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7">Agencies, hospitals, and independent clinicians orchestrate every {svc.name.toLowerCase()} episode on CaraLoom. Families come along for the ride — visit-by-visit, in real time.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/pricing" className="cn-btn bg-white text-cn-primary hover:bg-cn-soft" data-testid="service-cta-pricing">
              See operator pricing <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/auth" className="cn-btn border border-white/40 text-white hover:bg-white/10" data-testid="service-cta-get-started">
              Get matched as a family <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
